import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useMemo, useState } from "react";
import * as echarts from "echarts";
import type { BrandMetricsTrendPoint } from "@/services/api/brandMetricsTrend";
import { calculatePercentageYAxis } from "@/utils/chartHelpers";
import { formatPercent, formatMonthDay } from "@/utils/format";

export interface SentimentSelfChartProps {
  trend?: BrandMetricsTrendPoint[] | null;
  height?: number;
  title?: string;
}

const SentimentSelfChart: React.FC<SentimentSelfChartProps> = ({
  trend,
  height = 400,
  title,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("search_detail.sentiment_trend");
  const chartRef = useRef<HTMLDivElement>(null);
  const instRef = useRef<echarts.ECharts | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // 使用trend数据
  const chartData = useMemo(() => {
    if (trend && trend.length > 0) {
      // 使用trend数据，转换日期格式
      return trend.map((item) => ({
        day: formatMonthDay(item.date),
        positive: item.positiveMentionRate || 0,
        negative: item.negativeMentionRate || 0,
      }));
    }
    return [];
  }, [trend]);

  const option = useMemo(() => {
    // 计算动态Y轴范围
    const series = [
      { data: chartData.map((d) => d.positive) },
      { data: chartData.map((d) => d.negative) },
    ];
    const { min, max } = calculatePercentageYAxis(series);
    const isNarrow = containerWidth > 0 && containerWidth < 560;

    return {
      title: { show: false },
      tooltip: {
        trigger: "axis",
        formatter: (params: any[]) => {
          const axis =
            params?.[0]?.axisValueLabel || params?.[0]?.axisValue || "";
          const head = axis
            ? `<div style="margin-bottom:4px;font-weight:600;">${axis}</div>`
            : "";
          const body = (params || [])
            .map((p: any) => {
              const val =
                typeof p?.value === "number" ? p.value : Number(p?.value);
              const text = Number.isFinite(val) ? formatPercent(val, 1) : "--%";
              return `${p.marker} ${p.seriesName}: ${text}`;
            })
            .join("<br/>");
          return head + body;
        },
        axisPointer: {
          type: "line",
          snap: true,
          lineStyle: { type: "dashed", width: 2, color: "#6b5bd2" },
          z: 30,
        },
        backgroundColor: "rgba(255,255,255,0.95)",
        borderColor: "#e5e7eb",
        textStyle: { color: "#0f172a" },
      },
      legend: {
        data: [
          t("search_detail.insight_metric_positive"),
          t("search_detail.insight_metric_negative"),
        ],
        bottom: 12,
        itemWidth: 24,
        itemHeight: 24,
      },
      grid: { left: 5, right: 20, bottom: 60, containLabel: true },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: chartData.map((d) => d.day),
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: "#e5e7eb" },
        },
      },
      yAxis: {
        type: "value",
        min,
        max,
        axisLabel: { formatter: (val: number) => formatPercent(val, 1) },
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: "#e5e7eb" },
        },
      },
      series: [
        {
          name: t("search_detail.insight_metric_negative"),
          type: "line",
          smooth: true,
          data: chartData.map((d) => d.negative),
          itemStyle: { color: "#ef4444" },
          lineStyle: { color: "#ef4444", width: 3, cap: "round" },
          symbol: "circle",
          symbolSize: 8,
          areaStyle: {
            color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(239,68,68,0.12)" },
              { offset: 1, color: "rgba(239,68,68,0.02)" },
            ]),
          },
          emphasis: {
            focus: "series",
            itemStyle: { shadowBlur: 6, shadowColor: "#ef4444" },
          },
        },
        {
          name: t("search_detail.insight_metric_positive"),
          type: "line",
          smooth: true,
          data: chartData.map((d) => d.positive),
          itemStyle: { color: "#21C4B8" },
          lineStyle: { color: "#21C4B8", width: 3, cap: "round" },
          symbol: "circle",
          symbolSize: 8,
          areaStyle: {
            color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(87,191,146,0.15)" },
              { offset: 1, color: "rgba(87,191,146,0.02)" },
            ]),
          },
          emphasis: {
            focus: "series",
            itemStyle: { shadowBlur: 6, shadowColor: "#21C4B8" },
          },
        },
      ],
    };
  }, [chartData, effectiveTitle, containerWidth]);

  useEffect(() => {
    if (chartRef.current && !instRef.current) {
      instRef.current = echarts.init(chartRef.current);
      const onResize = () => instRef.current?.resize();
      window.addEventListener("resize", onResize);
      // 监听容器尺寸，确保按容器自适应
      const ro = new ResizeObserver(() => {
        if (chartRef.current) {
          setContainerWidth(chartRef.current.clientWidth);
          instRef.current?.resize();
        }
      });
      if (chartRef.current) {
        setContainerWidth(chartRef.current.clientWidth);
        ro.observe(chartRef.current);
      }
      return () => {
        window.removeEventListener("resize", onResize);
        ro.disconnect();
        instRef.current?.dispose();
        instRef.current = null;
      };
    }
  }, []);

  useEffect(() => {
    if (instRef.current) {
      try {
        instRef.current.clear();
      } catch {}
      try {
        instRef.current.setOption(
          { title: { show: false, text: "" } } as any,
          true
        );
      } catch {}
      instRef.current.setOption(option, true);
    }
  }, [option]);

  return (
    <div
      style={{
        width: "100%",
        height,
        background: "#ffffff",
        borderRadius: 6,
        border: "1px solid #e5e7eb",
        padding: "2px",
      }}
    >
      <div ref={chartRef} style={{ width: "100%", height: height }} />
    </div>
  );
};

export default SentimentSelfChart;
